import React, { useState } from "react";
import toast from "react-hot-toast";
import { gql, useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const RECREATE_PLANS = gql`
  mutation RecreatePricePlansInStripe {
    recreatePricePlansInStripe {
      accountId
      checked
      recreated
      failed
    }
  }
`;

const WEBHOOK_EVENTS = [
  "checkout.session.completed",
  "payment_intent.succeeded",
  "payment_intent.canceled",
  "charge.refunded",
  "charge.dispute.created",
  "charge.dispute.closed",
  "customer.subscription.created",
  "customer.subscription.updated",
  "customer.subscription.deleted",
  "invoice.paid",
  "invoice.payment_failed",
  "account.updated",
  "capability.updated",
];

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Moving the platform to another Stripe account. Everything Acolide has in
// the current account stays there, so this warns with the real numbers and
// needs the confirmation phrase typed before it does anything.
export default function ChangeStripeAccount({ call, inUse, onChanged, busy, setBusy }) {
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState("");
  const [publishable, setPublishable] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [connectWebhookSecret, setConnectWebhookSecret] = useState("");
  const [checked, setChecked] = useState(null);
  const [problems, setProblems] = useState(null);
  const [typed, setTyped] = useState("");
  const [done, setDone] = useState(null);
  const [recreatePlans, { loading: recreating, data: recreateData }] = useMutation(RECREATE_PLANS);

  const body = () => ({
    accountChange: true,
    stripeSecretKey: secret.trim(),
    stripePublishableKey: publishable.trim(),
    stripeWebhookSecret: webhookSecret.trim(),
    stripeConnectWebhookSecret: connectWebhookSecret.trim(),
  });

  const edit = (setter) => (e) => {
    setter(e.target.value);
    setChecked(null);
    setProblems(null);
    setTyped("");
  };

  const check = async () => {
    setBusy("account-check");
    setProblems(null);
    try {
      const j = await call("/admin/stripe/platform-settings/verify", { method: "POST", body: body() });
      if (j.ok) setChecked(j);
      else {
        setChecked(null);
        setProblems(j.problems || ["These keys can't be used."]);
      }
    } catch (e) {
      setProblems([e.message]);
    } finally {
      setBusy("");
    }
  };

  const change = async () => {
    setBusy("account-change");
    setProblems(null);
    try {
      const j = await call("/admin/stripe/platform-settings", { method: "PUT", body: { ...body(), confirmation: typed.trim() } });
      toast.success(`Platform moved to ${checked.accountChange.to}`);
      setDone({ to: checked.accountChange.to, from: checked.accountChange.from, moved: j.moved || {}, usage: j.usage || {} });
      setSecret("");
      setPublishable("");
      setWebhookSecret("");
      setConnectWebhookSecret("");
      setChecked(null);
      setTyped("");
      await onChanged();
    } catch (e) {
      setProblems(e.problems || [e.message]);
      toast.error("Account not changed");
    } finally {
      setBusy("");
    }
  };

  const runRecreate = async () => {
    try {
      const { data } = await recreatePlans();
      const r = data?.recreatePricePlansInStripe;
      if (r?.failed?.length) toast.error(`${r.failed.length} plan(s) couldn't be recreated`);
      else toast.success(r?.recreated?.length ? `Recreated ${plural(r.recreated.length, "plan", "plans")}` : "All plans are already in this account");
    } catch (e) {
      toast.error(e.message || "Couldn't recreate plans");
    }
  };

  const phrase = checked?.accountChange?.confirmationPhrase;
  const impact = checked?.impact;
  const recreateResult = recreateData?.recreatePricePlansInStripe;

  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "error.light" }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="error.main">
              Change Stripe account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Move Acolide to a different Stripe account. Only do this if the current account can't be used any more.
            </Typography>
          </Box>
          <Button color="error" variant={open ? "text" : "outlined"} onClick={() => setOpen(!open)}>
            {open ? "Close" : "Change account…"}
          </Button>
        </Stack>

        <Collapse in={open} unmountOnExit>
          <Stack spacing={2.5} sx={{ mt: 2.5 }}>
            <Alert severity="warning">
              <b>What moving accounts does.</b> Everything below lives in {inUse?.accountId || "the current account"} and
              stays there:
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                <li>Clinics already paying for a plan keep being billed in the old account and keep access. Their plan can't be changed or cancelled from Acolide; cancel each one in the old account's dashboard once they have subscribed again.</li>
                <li>Clinics connected to Stripe for card payments are disconnected and must connect again from Payment settings. Until they do, they can't take card payments; their card readers need setting up again.</li>
                <li>Saved cards for SMS/email billing and patients' saved customers start fresh in the new account.</li>
                <li>Plans must be recreated in the new account (a button appears after the move). SMS/email usage meters and prices are copied automatically.</li>
              </ul>
              In the new account, create <b>two</b> webhook endpoints pointing at this payments service's <code>/stripe/webhook</code> URL:
              one for this account's own events (plans, subscriptions, SMS/email billing) and one for connected-accounts events
              (clinics' card payments). Copy each endpoint's signing secret into the matching field below — Stripe signs the two
              streams with different secrets, so both are needed or one stream stops arriving.
            </Alert>

            <TextField label="New account secret key" type="password" autoComplete="new-password" value={secret} onChange={edit(setSecret)} fullWidth placeholder={`sk_${inUse?.livemode ? "live" : "test"}_… or rk_…`} />
            <TextField label="New account publishable key" value={publishable} onChange={edit(setPublishable)} fullWidth placeholder={`pk_${inUse?.livemode ? "live" : "test"}_…`} />
            <TextField label="Own-account events — webhook signing secret" type="password" autoComplete="new-password" value={webhookSecret} onChange={edit(setWebhookSecret)} fullWidth placeholder="whsec_…" helperText="The endpoint WITHOUT Connect events (plans, subscriptions, SMS/email billing). Dashboard > Developers > Webhooks > that endpoint > Signing secret" />
            <TextField label="Connected-accounts events — webhook signing secret" type="password" autoComplete="new-password" value={connectWebhookSecret} onChange={edit(setConnectWebhookSecret)} fullWidth placeholder="whsec_…" helperText="The endpoint WITH 'Listen to events on connected accounts' enabled (clinics' card payments, refunds, disputes)." />

            {problems ? (
              <Alert severity="error">
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </Alert>
            ) : null}

            <Box>
              <Button variant="outlined" color="error" onClick={check} disabled={!!busy || !secret.trim()}>
                {busy === "account-check" ? "Checking…" : "Check new account"}
              </Button>
            </Box>

            {checked && impact ? (
              <Stack spacing={1.5}>
                <Alert severity="error">
                  Moving from <b>{checked.accountChange.from}</b> to <b>{checked.accountChange.to}</b> ({checked.livemode ? "live" : "test"} mode) will affect:
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                    <li>{plural(impact.payingClinics, "clinic", "clinics")} paying for a plan (they stay billed in the old account)</li>
                    <li>{plural(impact.connectedClinics, "clinic", "clinics")} connected for card payments (they must reconnect)</li>
                    <li>{plural(impact.clinicsWithBillingCustomer, "clinic", "clinics")} with saved SMS/email billing details</li>
                    <li>{plural(impact.patientCustomers, "patient customer record", "patient customer records")}</li>
                  </ul>
                </Alert>
                <Typography variant="body2">
                  To confirm, type <b style={{ fontFamily: "monospace" }}>{phrase}</b>
                </Typography>
                <TextField value={typed} onChange={(e) => setTyped(e.target.value)} fullWidth placeholder={phrase} inputProps={{ spellCheck: false, autoComplete: "off" }} />
                <Box>
                  <Button variant="contained" color="error" onClick={change} disabled={!!busy || typed.trim() !== phrase}>
                    {busy === "account-change" ? "Moving…" : "Change Stripe account"}
                  </Button>
                </Box>
              </Stack>
            ) : null}
          </Stack>
        </Collapse>

        {done ? (
          <Alert severity="success" sx={{ mt: 2.5 }}>
            <b>Moved to {done.to}.</b> Next steps:
            <ol style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              <li>
                Recreate plans in the new account so clinics can subscribe.{" "}
                <Button size="small" variant="contained" onClick={runRecreate} disabled={recreating} sx={{ ml: 1 }}>
                  {recreating ? "Recreating…" : "Recreate plans"}
                </Button>
                {recreateResult ? (
                  <div style={{ marginTop: 4 }}>
                    Checked {recreateResult.checked}; recreated {recreateResult.recreated.length}
                    {recreateResult.failed.length ? `; failed: ${recreateResult.failed.join("; ")}` : ""}.
                  </div>
                ) : null}
              </li>
              <li>Make sure the new account's webhook endpoint sends: {WEBHOOK_EVENTS.join(", ")}.</li>
              <li>
                Tell the {plural(done.moved.connectedClinicsDisconnected || 0, "connected clinic", "connected clinics")} to reconnect
                Stripe in Payment settings.
              </li>
              <li>
                {plural(done.moved.subscriptionsKeptInOldAccount || 0, "plan subscription stays", "plan subscriptions stay")} billing
                in {done.from}: cancel each one there once the clinic has subscribed again.
              </li>
            </ol>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
